import { App, Plugin } from 'vue';
import { createRandomNumber } from './math';
import { camelCase } from 'change-case';

/**
 * 注册组件
 * @param component
 * @param alias
 * @returns
 */
export const withInstall = <T>(component: T, alias?: string) => {
    const comp = component as any;
    comp.install = (app: App) => {
        app.component(comp.name || comp.displayName, component as any);
        alias && (app.config.globalProperties['alias'] = component);
    };
    return component as T & Plugin;
};

/**
 * 获取 assets 静态资源
 * @param url /images/test.png images/test.png 均可
 * @returns
 */
export const getAssetsFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../assets/${url}`, import.meta.url).href;
};

/**
 * 获取图片资源 - 指向 assets/images/
 * @param url
 * @returns
 */
export const getImageFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../assets/images/${url}`, import.meta.url).href;
};

/**
 * 获取图片资源 - 指向 anim/
 * @param url
 * @returns
 */
export const getAniFile = (url: string): string => {
    url = url[0] === '/' ? url.slice(1) : url;
    return new URL(`../anim/${url}.json`, import.meta.url).href;
};

/**
 * 函数式 - 链接字符串
 * @param symbol
 * @returns
 */
export function joinParamsWithSymbol(symbol = ' ') {
    return function (params) {
        return Object.keys(params)
            .reduce((p, c) => [...p, `${c} ${params[c]}`], [])
            .join(symbol || ' ');
    };
}

export const joinParamsWithSpace = joinParamsWithSymbol();

/**
 * 同步阻塞
 * @param timeout 延时
 * @returns
 */
export function sleep(timeout = 200) {
    return new Promise((r) => setTimeout(r, timeout));
}

/**
 * 生成唯一 key
 * @param prefix key 前缀
 */
export function createKey(prefix: string = '') {
    return `${prefix ? prefix + '' : ''}-${(+new Date()).toString()}-${createRandomNumber()}`;
}

/**
 * 毫秒数转秒数
 * @param millisecond
 */
export function timeFormat(millisecond: number) {
    return (millisecond / 1000).toFixed(2);
}

/**
 * 对比版本
 * @param 1 version1>version2
 * @param 0 version1==version2
 * @param -1 version1<version2
 */
export function compareVersion(v1: string, v2: string): -1 | 0 | 1 {
    let sources: string[] = v1.split('.');
    let dests = v2.split('.');
    let maxL = Math.max(sources.length, dests.length);
    let result: -1 | 0 | 1 = 0;
    for (let i = 0; i < maxL; i++) {
        let preValue = sources.length > i ? sources[i] : '0';
        let preNum = isNaN(Number(preValue)) ? preValue.charCodeAt(0) : Number(preValue);
        let lastValue = dests.length > i ? dests[i] : '0';
        let lastNum = isNaN(Number(lastValue)) ? lastValue.charCodeAt(0) : Number(lastValue);
        if (preNum < lastNum) {
            result = -1;
            break;
        } else if (preNum > lastNum) {
            result = 1;
            break;
        }
    }
    return result;
}

/**
 * 首字母大写
 * @param param0
 * @returns
 */
export function capitalFirstWord([first, ...rest]: string): string {
    return first.toUpperCase() + rest.join('');
}

/**
 * 单层对象 key 转为小驼峰
 * @param payload
 */
export function shallowCamelCaseObject(payload: Object) {
    const result: any = {};
    for (let key in payload) {
        result[camelCase(key)] = payload[key];
    }
    return result;
}

/**
 * @returns
 */
export function getMs() {
    return Date.now();
}

/**
 * 获取 unix 时间戳
 * @returns
 */
export function getUnixTimestamp() {
    return Math.floor(new Date().getTime() / 1000);
}

/**
 * 判断是否在微信环境
 */
export function isInWxEnv(): boolean {
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.indexOf('micromessenger') !== -1;
}

/**
 * 判断浏览器环境是否支持 WASM
 * @returns
 */
export function isWasmSupported(): boolean {
    return (
        typeof WebAssembly === 'object' &&
        WebAssembly.validate(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00))
    );
}

/**
 * 删除指定字符的
 */
export function trimPayload(target: any, symbol: Array<any> = [undefined, '', null]) {
    const clone = Object.assign({}, target);
    Object.keys(clone).forEach((key) => {
        if (symbol.includes(clone[key])) delete clone[key];
    });
    return clone;
}
